import React, { useState } from 'react';
import GreyWrapper from '../../../common/GreyWrapper';
import { post_api } from '../../../../../../../redux/api_funcs';
import { get_api_url } from '../../../../../../../utils/urls.js';

interface SmsShareProps {
  smsContent: string;
  btnText: () => string;
  studentDetails: {
    student_uuid: string;
    message_dispatch_method: string;
  };
  title: string;
  onSend: (p: string) => void;
  isSmsSent: boolean;
}

const SmsShare: React.FC<SmsShareProps> = ({
  smsContent,
  btnText,
  studentDetails,
  title,
  onSend,
  isSmsSent
}) => {
  const [smsSent, setSmsSent] = useState<boolean>(!!isSmsSent);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const buttonText = btnText();
  const { student_uuid, message_dispatch_method } = studentDetails;
  const isDisabled =
    !!smsSent ||
    isLoading ||
    (message_dispatch_method !== 'whatsapp' && message_dispatch_method !== 'sms');

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(smsContent);
      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const onSendSMS = async () => {
    if (!student_uuid) {
      console.error('student_uuid is missing!');
      return;
    }

    setIsLoading(true);
    setError('');

    const plainMessage = smsContent;

    const api_url = '/v2/notifications/twilio/send_message';
    const url = get_api_url(api_url, false, true);

    const payload = {
      to_student_uuid: student_uuid,
      message: plainMessage,
      sms_template_tag: '',
      message_medium_type: message_dispatch_method === 'whatsapp' ? 'WHATSAPP' : 'SMS',
      original_message_uuid: ''
    };

    try {
      const response = await post_api(url, payload, true);

      // Check if the response indicates success or failure
      if (response && response.status === 'failure') {
        setError(response.reason || 'Failed to send message');
        setIsLoading(false);
        return;
      }

      setSmsSent(true);
      onSend('sms');
    } catch (err: any) {
      console.error('API error:', err);

      // Handle different types of errors
      let errorMessage = 'Failed to send message. Please try again.';

      if (err.response && err.response.data) {
        if (err.response.data.reason) {
          errorMessage = err.response.data.reason;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="flex flex-1">
        <GreyWrapper customStyle="flex flex-1 flex-col justify-between w-full">
          <div className="h-[74%]">
            <div className="flex items-center justify-between">
              <h3 className="text-[15px] font-[550] leading-[18px] text-[#333] mb-2">
                Share via {title.replace('Send ', '')}
              </h3>
              <div className="flex items-center gap-[5px] cursor-pointer" onClick={copyToClipboard}>
                <img src="https://staticdata.myyogateacher.com/teacher/copy.svg" />
                <p className="text-[12px] font-normal text-[#757575]">
                  {copied ? 'copied' : 'copy'}
                </p>
              </div>
            </div>
            <div className="message-content h-auto bg-white border border-gray-200 rounded-md p-4 text-[15px] font-medium leading-[20px] text-[#321A50] whitespace-pre-line">
              {smsContent}
            </div>
          </div>
          <div>
            <button
              className={`flex items-center justify-center m-0 mt-4 w-full text-[16px] py-2 rounded-[14px] outline-none border-0 font-[550] ${isDisabled ? ' cursor-not-allowed bg-white hover:text-[#EE731B] text-[#EE731B] border border-gray-200' : 'bg-[#EE731B] text-white hover:bg-[#CC4B11] hover:text-white cursor-pointer'}`}
              onClick={onSendSMS}
              disabled={isDisabled}
            >
              {isLoading ? (
                <span className="inline-flex items-center justify-center gap-[8px]">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      strokeDasharray="15.708"
                    ></circle>
                  </svg>
                  <span>Sending...</span>
                </span>
              ) : smsSent ? (
                <span className="inline-flex items-center justify-center gap-[8px]">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <g clipPath="url(#clip0_712_1767)">
                      <rect width="18" height="18" rx="9" fill="white" />
                      <path
                        d="M9 0C4.02891 0 0 4.02891 0 9C0 13.9711 4.02891 18 9 18C13.9711 18 18 13.9711 18 9C18 4.02891 13.9711 0 9 0ZM13.0711 7.44609L8.57109 11.9461C8.37773 12.1395 8.12813 12.2344 7.875 12.2344C7.62187 12.2344 7.37086 12.1388 7.17855 11.9465L4.92855 9.69652C4.545 9.31191 4.545 8.68788 4.92855 8.30327C5.31316 7.91866 5.93719 7.91866 6.3218 8.30327L7.875 9.85781L11.6789 6.05391C12.0635 5.6693 12.6875 5.6693 13.0721 6.05391C13.4543 6.43711 13.4543 7.06289 13.0711 7.44609Z"
                        fill="#EE731B"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_712_1767">
                        <rect width="18" height="18" rx="9" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  <span>{title.replace('Send ', '')} Sent</span>
                </span>
              ) : (
                buttonText
              )}
            </button>

            {/* Error Message Display */}
            {error && (
              <div>
                <p className="text-sm text-red-600 mt-1">{error}</p>
              </div>
            )}
          </div>
        </GreyWrapper>
      </div>
    </>
  );
};

export default SmsShare;
